import { hitProtectedBackendApi } from '@/app/endpoints/enpointsUtils'
import { ILoanQuote, IQuotationFormOptions, IGlobalComparatorOptions } from '@/app/models/loanQuote'
import { withConsentUrl } from '@/app/utils/consent-url'
import { LoanPdfExtractError } from '@/app/constants/loanPdfExtract'
import { generateSiteToken } from '@/app/utils/loan/loan-utils'
import { getCookie } from '@/app/utils/client-cookies'

const BASE_API = `${process.env.NEXT_PUBLIC_RM_BASE_API}/api/loan-comparator`
const VALIDATION_API = `${process.env.NEXT_PUBLIC_RM_BASE_API}/api/validate`
const LOAN_API = `${process.env.NEXT_PUBLIC_RM_BASE_API}/api/loan`

const getEndpoint = (path: string) => `${BASE_API}${path}`

const ENDPOINTS = {
    GET_QUOTE_FROM_TOKEN: (token: string) => getEndpoint(`/get-quote/${token}`),
    CREATE_QUOTE: getEndpoint('/create-quote'),
    // Route Symfony hors du préfixe loan-comparator
    CHECK_TOKEN_FROM_COOKIE: (token: string) => `${LOAN_API}/check-token-from-cookie/${token}`,
    UPDATE_QUOTE_STATUS: (token: string) => getEndpoint(`/update-quote-status/${token}`),
    POST_PROJECT_TYPE: (token: string) => getEndpoint(`/select-project-type/${token}`),
    POST_QUOTATION_FORM: (token?: string) =>
        token ? getEndpoint(`/quotation-form/${token}`) : getEndpoint('/quotation-form'),
    PATCH_BORROWER: getEndpoint('/update-borrower'),
    REGISTER_OFFER_CLICK: (token: string) => getEndpoint(`/register-offer-click/${token}`),
    PROMO_CODE: (token: string) => getEndpoint(`/promo-code/${token}`),
    GET_OFFERS: (token: string) => getEndpoint(`/get-quote-offers/${token}`),
    GET_OFFER_DATA: (token: string) => getEndpoint(`/get-offer-data/${token}`),
    GET_QUOTATION_FORM_OPTIONS: getEndpoint('/get-quotation-form-options'),
    GET_GLOBAL_OPTIONS: getEndpoint('/get-global-options'),
    CREATE_DELAYED_CALL_TASK: (token: string) => getEndpoint(`/create-delayed-call-task/${token}`),
    POST_QUOTATION_REQUEST: (token: string) => getEndpoint(`/quotation-request/${token}`),
    POST_SUBSCRIPTION_REQUEST: (token: string) => getEndpoint(`/subscription-request/${token}`),
    POST_SUBSCRIPTION_FORM: (token: string) => getEndpoint(`/subscription-form/${token}`),
    GET_QUOTATION_PDF: (token: string) => getEndpoint(`/quotation-pdf/${token}`),
    POST_LEAD_CHECK: (token: string) => getEndpoint(`/create-lead-check/${token}`),
    POST_PDF_EXTRACT: (token: string) => getEndpoint(`/pdf-extract/${token}`),
}

// Interface for loan submission format (different from ILoan which is for GET responses)
// Symfony expects: type: { value, label }, amount, duration, stages - all as strings
export interface ILoanSubmission {
    id?: string | null
    type: { value: number; label: string }
    reimbursement_delay?: boolean
    delay_type?: 'partial' | 'total'
    delay_month_duration?: number | null
    // amount* are in euros, amount*_alt in the currency given by selected_currency
    amount: string | null // Production sends strings
    amount_left?: string | null
    save_amount_left: boolean
    selected_currency?: string
    amount_left_alt?: string | null
    amount_alt?: string | null
    rate: string // Production sends string with decimals e.g. "1.00"
    duration: string // Production sends strings
    duration_left?: string | null
    initial_duration?: string | null
    stages?: Array<{ amount: string | null; amount_alt?: string | null; month_duration: string }>
}

// Interface for borrower submission format (different from IBorrower which is for GET responses)
// Symfony expects: cover_percentage, birthdate, job ({label, value}), type ('main'|'co'), options (object)
export interface IBorrowerSubmission {
    id?: number | null
    gender?: number | null
    firstname: string | null
    lastname: string | null
    birthdate: string | null
    email: string | null
    phone: string | null
    address: {
        street: string | null
        street_number?: string | null
        street_name?: string | null
        additional_address: string | null
        zip_code: string | null
        city: string | null
        country: string
        sub_area: string | null
    }
    smoker: boolean
    medical_risk: boolean
    rejected_by_insurance?: boolean
    job: { label: string | null; value: number } | null // Production format
    liberal_job?: { label: string | null; value: number } | null
    medical_job?: { label: string | null; value: number } | null
    job_trips?: boolean
    job_risk?: boolean
    job_risk_field?: { label: string | null; value: number } | null
    job_at_height?: boolean
    job_handler?: boolean
    job_clerk?: boolean
    job_exact_name?: string | null
    risky_sport?: boolean
    risky_sport_name?: string[] | null
    searching_job?: boolean
    is_expat: boolean
    type: 'main' | 'co'
    is_lemoine?: boolean
    has_side_loans?: boolean
    lastname_birth?: string | null
    city_birth?: string | null
    department_birth?: string | null
    family_status?: number | null
    cover_percentage: number
    options: {
        DC: boolean
        PTIA: boolean
        IPT_ITT: boolean
        IPP: boolean
        PLUS: boolean
    }
    working_height?: number
    annual_mileage?: number
    [key: string]: unknown // Allow additional fields
}

// Interface for quotation form payload (matches Symfony backend expectations)
export interface IQuotationFormPayload {
    borrowers: IBorrowerSubmission[]
    loans: ILoanSubmission[]
    project_type: string
    project_purpose: number
    is_loan_repurchase?: boolean
    external_delegation?: boolean | null
    current_insurance_company?: string | null
    selected_currency: string
    bank: {
        name: string | null
        custom_bank_name?: string | null
        signature_date: string | null
        required_guarantees?: string[]
        loan_was_renegotiated?: boolean
        renegotiation_date?: string | null
        address?: {
            street?: string | null
            additional_address?: string | null
            zip_code?: string | null
            city?: string | null
            country?: string | null
        }
        agency?: string | null
    }
    abtest?: Record<string, unknown>
    is_first_quotation?: boolean
}

// Interface for quotation form response
export interface IQuotationFormResponse {
    token: string
    quote?: ILoanQuote
}

/**
 * Validate a phone number via the backend API (CaptainVerify in prod)
 */
export async function validatePhone(phone: string): Promise<boolean> {
    try {
        const clean = phone.replace(/\s/g, '')
        const response = await fetch(`${VALIDATION_API}/phone/${encodeURIComponent(clean)}`)
        if (!response.ok) return true // fail open
        const data = await response.json()
        // Current API returns `{ phone, isValid, reason }`; older loan
        // responses used `{ valid }`. Prefer the new key, fall back to the
        // legacy one, and treat missing keys as valid (fail open).
        return (data?.isValid ?? data?.valid) !== false
    } catch {
        return true // fail open on network errors
    }
}

/**
 * Validate an email via the backend API (anti-spam rules + CaptainVerify in prod)
 */
export async function validateEmail(email: string): Promise<boolean> {
    try {
        const clean = email.trim().toLowerCase()
        const response = await fetch(`${VALIDATION_API}/email/${encodeURIComponent(clean)}`)
        if (!response.ok) return true
        const data = await response.json()
        // Same dual-key handling as `validatePhone` — see comment there.
        return (data?.isValid ?? data?.valid) !== false
    } catch {
        return true
    }
}

/**
 * Create a delayed call task in Auctor CRM (fire and forget)
 */
export async function createDelayedCallTask(token: string): Promise<void> {
    try {
        await hitProtectedBackendApi(ENDPOINTS.CREATE_DELAYED_CALL_TASK(token), { method: 'POST', cache: false })
    } catch {
        // Fire and forget
    }
}

export async function getLoanQuoteFromToken(token: string): Promise<ILoanQuote> {
    return await hitProtectedBackendApi(ENDPOINTS.GET_QUOTE_FROM_TOKEN(token), { cache: false })
        .then(async (response) => {
            if (!response.ok) {
                throw new Error(`HTTP error ${response.status}`)
            }
            const data = await response.json()
            if (data?.code || !data?.token) {
                throw new Error(data?.message || 'Invalid quote response')
            }
            return data
        })
        .catch((e) => {
            throw new Error('Error fetching quote', { cause: e })
        })
}

export interface ILoanPdfExtractResponse {
    code: string
    success?: boolean
    extract_id?: number
    message?: string
}

export async function postLoanPdfExtract(
    token: string,
    payload: { pdf_base64: string; pdf_name: string },
    signal?: AbortSignal
): Promise<ILoanPdfExtractResponse> {
    const response = await hitProtectedBackendApi(
        ENDPOINTS.POST_PDF_EXTRACT(token),
        { method: 'POST', tokenPrefix: `pdf_extract_${token}`, signal },
        payload
    )
    const data = await response.json().catch(() => null)

    if (!response.ok || !data?.success) {
        // Un 413 est rendu par le serveur web avant Symfony : pas de corps JSON, donc pas de code
        const fallback = response.status === 413 ? 'PAYLOAD_TOO_LARGE' : 'EXTRACTION_FAILED'
        throw new LoanPdfExtractError(data?.code || fallback, data?.message)
    }

    return data
}

export async function createLoanQuote(): Promise<ILoanQuote> {
    return await hitProtectedBackendApi(ENDPOINTS.CREATE_QUOTE, {
        method: 'POST',
        cache: false,
        tokenPrefix: 'quote_creation',
    })
        .then(async (response) => {
            if (!response.ok) {
                throw new Error(`HTTP error ${response.status}`)
            }
            const data = await response.json()
            if (data?.code || !data?.token) {
                throw new Error(data?.message || 'Invalid quote response')
            }
            return data
        })
        .catch((e) => {
            throw new Error('Error creating quote', { cause: e })
        })
}

export async function checkTokenFromCookie(token: string): Promise<boolean> {
    return await hitProtectedBackendApi(ENDPOINTS.CHECK_TOKEN_FROM_COOKIE(token), { cache: false })
        .then(async (response) => {
            // 204 = devis introuvable, souscrit ou expiré
            if (!response.ok || response.status === 204) return false
            const data = await response.json()
            return !!data && !data.code
        })
        .catch(() => false)
}

export async function updateQuoteStatus(token: string, status: string): Promise<void> {
    return await hitProtectedBackendApi(
        ENDPOINTS.UPDATE_QUOTE_STATUS(token),
        { method: 'PATCH', cache: false },
        { status }
    )
        .then(async (response) => {
            if (!response.ok) {
                throw new Error(`HTTP error ${response.status}`)
            }
            await response.json()
        })
        .catch((e) => {
            throw new Error('Error updating quote status', { cause: e })
        })
}

export async function postProjectType(token: string, projectType: string, isLoanRepurchase = false): Promise<void> {
    return await hitProtectedBackendApi(
        ENDPOINTS.POST_PROJECT_TYPE(token),
        { method: 'POST', cache: false },
        { project_type: projectType, is_loan_repurchase: isLoanRepurchase }
    )
        .then(async (response) => {
            if (!response.ok) {
                throw new Error(`HTTP error ${response.status}`)
            }
            await response.json()
        })
        .catch((e) => {
            throw new Error('Error posting project type', { cause: e })
        })
}

export async function getQuotationFormOptions(): Promise<IQuotationFormOptions> {
    return await hitProtectedBackendApi(ENDPOINTS.GET_QUOTATION_FORM_OPTIONS, { cache: false })
        .then(async (response) => {
            if (!response.ok) {
                throw new Error(`HTTP error ${response.status}`)
            }
            const data = await response.json()
            return data
        })
        .catch((e) => {
            throw new Error('Error fetching quotation form options', { cause: e })
        })
}

export async function getGlobalComparatorOptions(): Promise<IGlobalComparatorOptions> {
    return await hitProtectedBackendApi(ENDPOINTS.GET_GLOBAL_OPTIONS, { cache: false })
        .then(async (response) => {
            if (!response.ok) {
                throw new Error(`HTTP error ${response.status}`)
            }
            const data = await response.json()
            return data
        })
        .catch((e) => {
            throw new Error('Error fetching global comparator options', { cause: e })
        })
}

/**
 * Post the quotation form data to the backend
 * This will create or update a quote with all borrower, loan, and bank information
 *
 * @param payload - The quotation form data
 * @param token - Optional existing quote token (for updates)
 * @returns The response with the quote token
 */
export async function postQuotationForm(
    payload: IQuotationFormPayload,
    token?: string
): Promise<IQuotationFormResponse> {
    // Token format must match Symfony's ApiTokenValidatorService.php
    // See: assets/js/common/utils.js -> getPostQuotationFormSiteToken()
    const tokenPrefix = token ? `post_quotation_form${token}` : 'post_quotation_form'

    return await hitProtectedBackendApi(
        ENDPOINTS.POST_QUOTATION_FORM(token),
        {
            method: 'POST',
            cache: false,
            tokenPrefix,
        },
        payload
    )
        .then(async (response) => {
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}))
                throw new Error(errorData.message || `HTTP error ${response.status}`)
            }
            const data = await response.json()
            return data
        })
        .catch((e) => {
            console.error('Error posting quotation form:', e)
            throw new Error(e.message || 'Error posting quotation form')
        })
}

// Interface for identity gateway borrower update.
// Sends the full borrower payload (parité Vue postUpdateBorrowers) so the backend
// `loanComparator` validation group keeps all required fields and avoids a 422.
export interface IBorrowerIdentityUpdate {
    token: string
    trigger_contact_workflow?: boolean
    whatsapp_optin?: boolean
    data_sharing_optin?: boolean
    accept_phone_contact?: boolean
    accept_email_contact?: boolean
    accept_email_tracking?: boolean
    consent_wording_version?: string
    [key: string]: unknown
}

/**
 * Update borrower identity information
 * Used by the Identity Gateway page
 */
export interface IColdLeadEstimate {
    estimatedCommission: number | null
    currency: string
}

export async function updateBorrowerIdentity(payload: IBorrowerIdentityUpdate): Promise<IColdLeadEstimate | null> {
    return await hitProtectedBackendApi(
        ENDPOINTS.PATCH_BORROWER,
        {
            method: 'PATCH',
            cache: false,
        },
        withConsentUrl(payload)
    )
        .then(async (response) => {
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}))
                throw new Error(errorData.message || `HTTP error ${response.status}`)
            }
            const data = await response.json().catch(() => null)
            return data && typeof data.estimatedCommission === 'number'
                ? { estimatedCommission: data.estimatedCommission, currency: data.currency }
                : null
        })
        .catch((e) => {
            console.error('Error updating borrower:', e)
            throw new Error(e.message || 'Error updating borrower')
        })
}

export interface IOfferClickCommission {
    value: number
    currency: string
}

/**
 * Fige la commission RM de l'offre choisie côté serveur (valeur de conversion précise, base de la
 * remontée LTV offline) et renvoie la valeur pour l'event TikTok. Best-effort : le tracking
 * ne doit jamais casser le tunnel, on renvoie null en cas d'échec.
 */
export async function registerOfferClick(
    token: string,
    monthly: number,
    offerName: string
): Promise<IOfferClickCommission | null> {
    if (!token) {
        return null
    }

    return await hitProtectedBackendApi(ENDPOINTS.REGISTER_OFFER_CLICK(token), { method: 'POST' }, { monthly, offerName })
        .then(async (response) => {
            if (!response.ok) {
                return null
            }
            const data = await response.json().catch(() => null)
            return data && typeof data.value === 'number' ? { value: data.value, currency: data.currency } : null
        })
        .catch(() => null)
}

// Interface for loan offers response
export interface ILoanOffer {
    name: string
    displayName: string
    api: string
    image: string
    showOfferNameIfLogo: boolean
    // Raw numeric values for calculations
    monthlyRaw: number
    totalRaw: number
    rateRaw: string
    // Year-based pricing (8-year average)
    yearMonthlySumAsked: number | null
    yearSumAsked: number | null
    // Rate info
    rateTypeSentence: string | null
    rateTypeDescription: string | null
    monthlyPriceDescription: string | null
    // Savings vs bank offer
    saved: number
    // Medical
    onlySimpleMedicalFormalities: boolean
    medicalFormalities: {
        common?: Record<string, { title: string; subtitle?: string; description: string }>
        main?: Record<string, { title: string; subtitle?: string; description: string }>
        co?: Record<string, { title: string; subtitle?: string; description: string }>
    } | null
    // Schedule data keyed by year; first/second/sum are formatted French strings ("1 234,56")
    tab: Record<
        string,
        {
            first: string
            second: string
            sum: string
            sumRaw: number
            sumUntilNow: number
            average: number
        }
    > | null
    detailedLoanSchedule?: unknown[]
    // Guarantees
    guaranteesSummary: Record<string, unknown> | null
    guarantees?: string[]
    // Fees (RM application fee, gross and net of the promo code)
    handlingFee: number | null
    rmFeeAmount?: number
    promoDiscountAmount?: number
    rmFeeAmountAfterPromo?: number
    totalWithoutRmFee?: number
    rmFeeIncludedInYearSum?: boolean
    promoApplicable?: boolean
    // Exclusion
    exclusionReasons: string[]
    // Legacy fields (kept for backward compatibility)
    rate?: number
    rateType?: number
    monthly?: string
    total?: string
    medicalTests?: string[]
    isNotApplicable?: boolean
    notApplicableReason?: string
}

export interface INotApplicableOffer {
    name: string
    displayName: string
    contractName: string
    image: string
    exclusionReasons: string[]
    rateTypeSentence?: string | null
}

export interface ILoanOffersResponse {
    offers: ILoanOffer[]
    notApplicableOffers: INotApplicableOffer[]
    comparisonDuration?: number
}

/**
 * Get loan insurance offers for a quote
 */
export async function getOffers(token: string): Promise<ILoanOffersResponse> {
    const tokenPrefix = `get_offers_${token}`

    return await hitProtectedBackendApi(ENDPOINTS.GET_OFFERS(token), {
        method: 'GET',
        cache: false,
        tokenPrefix,
    })
        .then(async (response) => {
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}))
                throw new Error(errorData.message || `HTTP error ${response.status}`)
            }
            const data = await response.json()
            return data
        })
        .catch((e) => {
            console.error('Error getting offers:', e)
            throw new Error(e.message || 'Error getting offers')
        })
}

/**
 * Get detailed offer data (medical tests, schedule, etc.)
 */
export async function getOfferData(token: string): Promise<unknown> {
    return await hitProtectedBackendApi(ENDPOINTS.GET_OFFER_DATA(token), {
        method: 'GET',
        cache: false,
    })
        .then(async (response) => {
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}))
                throw new Error(errorData.message || `HTTP error ${response.status}`)
            }
            const data = await response.json()
            return data
        })
        .catch((e) => {
            console.error('Error getting offer data:', e)
            throw new Error(e.message || 'Error getting offer data')
        })
}

/**
 * Post quotation request (triggers PDF generation + email).
 * Le backend exige borrowers + chosen_offer (RequestParam nullable=false).
 */
export async function postQuotationRequest(
    token: string,
    data: { borrowers: Record<string, unknown>[]; chosen_offer: ILoanOffer }
): Promise<void> {
    return await hitProtectedBackendApi(ENDPOINTS.POST_QUOTATION_REQUEST(token), { method: 'POST', cache: false }, data)
        .then(async (response) => {
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}))
                throw new Error(errorData.message || 'Erreur lors de la demande de devis')
            }
        })
        .catch((e) => {
            console.error('Error posting quotation request:', e)
            throw e
        })
}

/**
 * Post subscription request (open dossier)
 */
export async function postSubscriptionRequest(
    token: string,
    offerData?: Record<string, unknown>
): Promise<{ token: string; [key: string]: unknown }> {
    return await hitProtectedBackendApi(
        ENDPOINTS.POST_SUBSCRIPTION_REQUEST(token),
        { method: 'POST', cache: false },
        offerData
    )
        .then(async (response) => {
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}))
                const error = new Error(errorData.message || 'Erreur lors de la souscription') as Error & {
                    response?: { data?: { code?: string; offer_display_name?: string } }
                }
                error.response = { data: errorData }
                throw error
            }
            return await response.json()
        })
        .catch((e) => {
            console.error('Error posting subscription request:', e)
            throw e
        })
}

/**
 * Get the URL for downloading the quotation PDF
 */
export function getQuotationPdfUrl(token: string): string {
    return ENDPOINTS.GET_QUOTATION_PDF(token)
}

// Blob same-origin : contrairement à l'URL cross-origin, un téléchargement lancé après un await n'est jamais bloqué comme une pop-up.
export async function fetchQuotationPdfBlob(token: string): Promise<Blob> {
    // Aucun en-tête custom : la route est publique par token, ce qui évite un préflight CORS.
    const url = getQuotationPdfUrl(token)

    for (let attempt = 0; attempt < 2; attempt++) {
        if (attempt > 0) {
            await new Promise((resolve) => setTimeout(resolve, 1000))
        }

        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 15000)

        try {
            const response = await fetch(url, { signal: controller.signal })

            if (!response.ok) {
                throw new Error(`Erreur lors du téléchargement du devis (${response.status})`)
            }

            // Le backend répond 200 avec un corps vide tant que le PDF n'est pas dans le bucket : on retente une fois.
            const blob = await response.blob()
            if (blob.size > 0) {
                return blob
            }
        } finally {
            clearTimeout(timeoutId)
        }
    }

    throw new Error('Devis indisponible')
}

/**
 * Create lead check for BPSI. Returns true when the lead check succeeded (response.ok).
 */
export async function postLeadCheckCreation(token: string): Promise<boolean> {
    try {
        const response = await hitProtectedBackendApi(ENDPOINTS.POST_LEAD_CHECK(token), {
            method: 'POST',
            cache: false,
        })
        return response.ok
    } catch {
        return false
    }
}

export interface ISubscriptionBorrower {
    id: number
    gender: number
    lastname: string
    firstname: string
    email: string
    phone: string
    address: {
        street: string
        street_number: string
        street_name: string
        zip_code: string
        city: string
        country: string
    }
    job_exact_name?: string
    politically_exposed?: boolean
    close_familly_politically_exposed?: boolean
    politically_exposed_firstname?: string
    politically_exposed_lastname?: string
    politically_exposed_function?: string
    lastname_birth?: string
    city_birth?: string
    department_birth?: string
    family_status?: number | null
}

export interface ISubscriptionFormPayload {
    borrowers: ISubscriptionBorrower[]
    bank: {
        name: string | null
        agency?: string | null
        address: { street: string | null; zip_code: string | null; city: string | null }
    }
    moral_person?: {
        name: string
        address: { street: string; zip_code: string; city: string }
    } | null
    needs_callback: boolean
    whatsapp_optin: boolean
    already_insured_from_company: boolean
    external_insurance_sign_date: string | null
    chosen_offer: Record<string, unknown> | null
}

export interface ISubscriptionFormResponse {
    partner_url?: string
    member_data?: { APIS: unknown; NEXT_STEPS: unknown }
}

/**
 * Post the full subscription form to complete the adhesion
 */
export async function postSubscriptionForm(
    token: string,
    payload: ISubscriptionFormPayload
): Promise<ISubscriptionFormResponse> {
    return await hitProtectedBackendApi(
        ENDPOINTS.POST_SUBSCRIPTION_FORM(token),
        { method: 'POST', cache: false },
        payload
    )
        .then(async (response) => {
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}))
                const error = new Error(errorData.message || 'Erreur lors de la souscription') as Error & {
                    response?: { data?: { code?: string; message?: string; offer_display_name?: string } }
                }
                error.response = { data: errorData }
                throw error
            }
            return await response.json()
        })
        .catch((e) => {
            console.error('Error posting subscription form:', e)
            throw e
        })
}

/**
 * Half-hour slots already booked for a given date, as the `HHMM` values sent in appointment_time ("0930", "1000"…).
 */
export async function getOccupiedCallbackHours(date: string, product: string = 'loan'): Promise<string[]> {
    const url = `${process.env.NEXT_PUBLIC_RM_BASE_API}/api/common/available-callback-slots?date=${encodeURIComponent(date)}&product=${encodeURIComponent(product)}`
    try {
        const response = await fetch(url)
        if (!response.ok) return []
        const data = await response.json()
        if (!Array.isArray(data?.availableSlots)) return []
        return data.availableSlots
            .filter(
                (slot: { value?: number; available?: boolean }) =>
                    slot?.available === false && typeof slot.value === 'number'
            )
            .map((slot: { value: number }) => String(slot.value).padStart(4, '0'))
    } catch {
        return []
    }
}

/**
 * Create a subscription reminder (email sent after 15 minutes if form is not submitted)
 */
export async function postSubscriptionReminderCreation(token: string): Promise<void> {
    try {
        await fetch(
            `${process.env.NEXT_PUBLIC_RM_BASE_API}/api/loan-comparator/create-subscription-reminder/${token}`,
            {
                method: 'POST',
            }
        )
    } catch {
        // Fire and forget
    }
}

/**
 * Post a callback request to Auctor CRM
 */
export async function postCallbackRequest(data: {
    first_name: string
    last_name: string
    phone: string
    appointment_date: string
    appointment_time: string
    product_type: string
    token: string
    from_link: string
    type: string
}): Promise<void> {
    const CALLBACK_URL = `${process.env.NEXT_PUBLIC_RM_BASE_API}/api/common/callback`
    try {
        await fetch(CALLBACK_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
        })
    } catch {
        // Fire and forget
    }
}

export type PromoCodeRejectionReason =
    | 'unknown'
    | 'inactive'
    | 'not_started'
    | 'expired'
    | 'exhausted'
    | 'user_limit'
    | 'not_applicable'

export interface IPromoCodeResult {
    valid: boolean
    reason: PromoCodeRejectionReason | null
    code: string | null
    label: string | null
    discountType: 'fixed_amount' | 'percent' | null
    discountValue: number | null
    feesAmount: number
    discountAmount: number
    feesAmountAfterDiscount: number
}

export class PromoCodeTooManyAttemptsError extends Error {
    constructor() {
        super('Too many promo code attempts')
        this.name = 'PromoCodeTooManyAttemptsError'
    }
}

export async function applyPromoCode(token: string, code: string, email?: string): Promise<IPromoCodeResult> {
    const response = await hitProtectedBackendApi(
        ENDPOINTS.PROMO_CODE(token),
        { method: 'POST' },
        { code, ...(email ? { email } : {}) }
    )
    if (response.status === 429) {
        throw new PromoCodeTooManyAttemptsError()
    }
    if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`)
    }
    const data = await response.json()
    return data.promo as IPromoCodeResult
}

export async function removePromoCode(token: string): Promise<void> {
    const response = await hitProtectedBackendApi(ENDPOINTS.PROMO_CODE(token), { method: 'DELETE' })
    if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`)
    }
}

export interface ILandingQuoteIdentity {
    email: string
    firstname: string
    lastname: string
    phone: string
}

// A landing callback starts without a quote: the back creates one in « rappel demandé » status and returns its token
export async function createLoanQuoteFromLanding(identity: ILandingQuoteIdentity): Promise<string> {
    const response = await fetch(getEndpoint('/create-quote-from-landing'), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-Site-Token': generateSiteToken('quote_creation'),
        },
        credentials: 'include',
        body: JSON.stringify({
            ...identity,
            utmTrackingToken: getCookie('rm_user_token') || getCookie('utm_tracking_token') || null,
        }),
    })
    if (!response.ok) {
        throw new Error(`Error creating landing quote: ${response.statusText}`)
    }
    const data = await response.json()
    if (typeof data?.token !== 'string' || !data.token) {
        throw new Error('Landing quote created without token')
    }
    return data.token
}
